import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Dialog,
  Icon,
  TextField,
  Layout,
  SpinnerWheel,
  Button,
  Banner,
  RadioGroup,
  Radio,
} from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { isEmpty } from 'core/utils/array'

import { useGetCarrierTractors } from '../../hooks/useGetCarrierTractors'
import { useUpdateShipmentTractorNumber } from '../../hooks/useUpdateShipmentTractorNumber'

type TruckSelectionProps = {
  readonly carrierUUID: string
  readonly shipmentUUID: string
  readonly tractorNumber?: string
  readonly onCancel: () => void
  readonly onAddNewTruck: () => void
  readonly onTruckAssigned: () => void
}

export function TruckSelection({
  carrierUUID,
  shipmentUUID,
  tractorNumber,
  onCancel,
  onAddNewTruck,
  onTruckAssigned,
}: TruckSelectionProps) {
  const { tractors, requestStatus, fetchTractors } = useGetCarrierTractors(carrierUUID)
  const { updateTractor, requestStatus: saveStatus } = useUpdateShipmentTractorNumber()
  const [selectedTractorNumber, setSelectedTractorNumber] = useState(tractorNumber)
  const [search, setSearch] = useState('')

  const filteredTractors = useMemo(() => {
    return tractors?.filter(tractor =>
      tractor.tractorNumber.toLowerCase().includes(search.toLowerCase())
    )
  }, [tractors, search])

  useEffect(() => {
    if (saveStatus === RequestStatus.Done) {
      onTruckAssigned()
    }
  }, [onTruckAssigned, saveStatus])

  useEffect(() => {
    if (saveStatus === RequestStatus.Error) {
      toast.error('Failed to assign the truck')
    }
  }, [onTruckAssigned, saveStatus])

  async function handleSave() {
    if (!selectedTractorNumber) return

    await updateTractor({
      shipmentUUID,
      tractorNumber: selectedTractorNumber,
    })
  }

  return (
    <>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          {tractorNumber && (
            <Banner>
              <Banner.Title>Before you change</Banner.Title>
              <Banner.Description>
                Changing the truck number will affect the current tracking and the new info will be
                sent to the shipper.
              </Banner.Description>
            </Banner>
          )}

          <TextField
            type="search"
            data-testid="search-truck"
            placeholder="Search for a truck"
            leading={<Icon name="search" size="16px" color="color-text-tertiary" />}
            onInput={event => {
              setSearch((event.target as HTMLInputElement).value)
            }}
            onClear={() => setSearch('')}
            clearable
          />

          {requestStatus === RequestStatus.Pending && (
            <Layout.Group align="center" justify="center" style={{ height: '100%' }}>
              <SpinnerWheel size="32px" title="Loading..." />
            </Layout.Group>
          )}

          {requestStatus === RequestStatus.Error && (
            <Banner variant="danger">
              <Banner.Title>Failed to load the trucks</Banner.Title>
              <Banner.Actions>
                <Banner.ActionPrimary onClick={() => fetchTractors({ carrierUUID })}>
                  Retry
                </Banner.ActionPrimary>
              </Banner.Actions>
            </Banner>
          )}

          {requestStatus === RequestStatus.Done && (
            <>
              {!isEmpty(filteredTractors) && (
                <RadioGroup
                  value={selectedTractorNumber}
                  onChange={event => setSelectedTractorNumber(event.detail.value)}
                >
                  {filteredTractors?.map(tractor => (
                    <Radio key={tractor.tractorNumber} value={tractor.tractorNumber}>
                      {tractor.tractorNumber}
                    </Radio>
                  ))}
                </RadioGroup>
              )}

              <Button
                style={{ alignSelf: 'flex-start' }}
                variant="tertiary"
                size="small"
                leading={<Icon name="plus" size="12px" color="color-primary-100" />}
                onClick={onAddNewTruck}
              >
                Add new truck
              </Button>
            </>
          )}
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onCancel}>Cancel</Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={handleSave}>Confirm</Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}
